import type {
  MailProviderDescriptor,
  MailProviderId,
} from '@agentic-flow-studio/shared/lib/mail-provider-availability';
import { Check, Clock, Mail, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button } from '../../ui/button';

interface MailProviderConnectActionsProps {
  descriptor: MailProviderDescriptor;
  /** True for the first provider in the returned order — it gets the primary treatment. */
  isPrimary: boolean;
  isConnecting: boolean;
  onConnect: (path: string, query?: Record<string, string>) => void;
  hasJoinedWaitlist: boolean;
  isJoiningWaitlist: boolean;
  onJoinWaitlist: (provider: MailProviderId) => void;
}

/**
 * The actions for one mailbox provider, chosen by its availability.
 *
 * A waitlisted provider is neither hidden nor left as a button that errors: it stays in the
 * list, says it is not open yet, and offers the only thing that is useful — being told when
 * it is. Hiding it would make a supported provider look unsupported; leaving the connect
 * button would fail at the server gate after the user had already committed.
 */
export function MailProviderConnectActions({
  descriptor,
  isPrimary,
  isConnecting,
  onConnect,
  hasJoinedWaitlist,
  isJoiningWaitlist,
  onJoinWaitlist,
}: MailProviderConnectActionsProps) {
  const { provider, label, availability } = descriptor;

  if (availability === 'unavailable') {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl border border-border/50 bg-muted/10">
        <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
        <div className="text-sm text-muted-foreground">
          {label} is not available in this workspace.
        </div>
      </div>
    );
  }

  if (availability === 'waitlist') {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-dashed border-border/70 bg-muted/10">
        <div className="flex items-start gap-3 flex-1">
          <Clock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-sm">{label} — coming soon</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {hasJoinedWaitlist
                ? 'You are on the list. We will email you as soon as it opens.'
                : 'Not open for new connections yet. Join the waitlist and we will let you know.'}
            </p>
          </div>
        </div>
        {hasJoinedWaitlist ? (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground sm:shrink-0">
            <Check className="h-4 w-4" />
            On the waitlist
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="sm:shrink-0 rounded-lg"
            onClick={() => onJoinWaitlist(provider)}
            disabled={isJoiningWaitlist}
          >
            {isJoiningWaitlist ? 'Joining…' : 'Join the waitlist'}
          </Button>
        )}
      </div>
    );
  }

  const buttonClass = isPrimary
    ? 'flex-1 rounded-xl h-12 text-md transition-all hover:scale-[1.02] active:scale-[0.98]'
    : 'flex-1 rounded-xl h-12 text-md border-primary/20 hover:bg-primary/5 transition-all hover:scale-[1.02] active:scale-[0.98]';

  /**
   * The read-only / full split is Google-only. Gmail has a read-only scope that still
   * permits labelling, so the choice is meaningful there. Graph has no such middle ground —
   * the scopes that let us categorise and archive are the ones that let us read — so Outlook
   * gets one button rather than a read-only option that would be a lie.
   */
  if (provider === 'google') {
    return (
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={() => onConnect('/api/connect/google/url', { type: 'readonly' })}
          disabled={isConnecting}
          variant={isPrimary ? 'default' : 'outline'}
          className={buttonClass}
        >
          <ShieldCheck className="h-5 w-5 mr-2" />
          Connect Gmail (Read-only)
        </Button>
        <Button
          variant="outline"
          onClick={() => onConnect('/api/connect/google/url', { type: 'full' })}
          disabled={isConnecting}
          className="flex-1 rounded-xl h-12 text-md border-primary/20 hover:bg-primary/5 transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <ShieldAlert className="h-5 w-5 mr-2 text-primary" />
          Connect Gmail (Full Access)
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-4">
      <Button
        onClick={() => onConnect('/api/connect/microsoft/url')}
        disabled={isConnecting}
        variant={isPrimary ? 'default' : 'outline'}
        className={buttonClass}
      >
        <Mail className={`h-5 w-5 mr-2 ${isPrimary ? '' : 'text-primary'}`} />
        Connect Outlook
      </Button>
    </div>
  );
}
